import { Modal } from 'bootstrap';
import htmx from 'htmx.org';
import type TomSelect from 'tom-select';

type EnhancedSelect = HTMLSelectElement & { tomselect?: TomSelect };
const dialog = document.getElementById('patient-search-dialog');
const dialogBody = document.getElementById('patient-search-dialog-body');
let trigger: HTMLElement | null = null;
let activeRequest: unknown;
let clientsRequest: AbortController | null = null;
let loadingDialog = false;

function focusQuery() {
  if (dialog?.classList.contains('show'))
    dialog.querySelector<HTMLInputElement>('#patients-query')?.focus({ preventScroll: true });
}

async function loadSearch(params = new URLSearchParams()) {
  if (!dialogBody || loadingDialog) return;
  loadingDialog = true;
  clientsRequest?.abort();
  const form = dialogBody.querySelector('[data-patients-search]');
  if (form) htmx.trigger(form, 'htmx:abort');
  const error = document.querySelector<HTMLElement>('[data-patients-dialog-error]');
  if (error) error.hidden = true;
  dialogBody.innerHTML = '<div class="patients-dialog-loading" role="status"><span class="spinner-border spinner-border-sm" aria-hidden="true"></span> Abriendo buscador…</div>';
  try {
    await htmx.ajax('GET', `/pacientes/buscador?${params}`, { target: dialogBody, swap: 'innerHTML' });
  } catch {
    if (error) error.hidden = false;
  } finally {
    loadingDialog = false;
    focusQuery();
  }
}

function openSearch(source?: HTMLElement, url?: URL) {
  if (!dialog || !dialogBody) return;
  trigger = source ?? null;
  const menuResults = document.getElementById('navigation-search-results');
  if (menuResults) menuResults.hidden = true;
  document.querySelector('[data-navigation-search]')?.setAttribute('aria-expanded', 'false');
  if (document.body.classList.contains('sidebar-open')) document.getElementById('sidebar-overlay')?.click();
  Modal.getOrCreateInstance(dialog).show();
  const params = url ? new URLSearchParams(url.search) : new URLSearchParams();
  if (url?.pathname === '/pacientes/buscar') params.set('advanced', 'true');
  if (!dialogBody.querySelector('[data-patients-search]') || params.size > 0) void loadSearch(params);
  else focusQuery();
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const element = event.target as Element | null;
  const link = element?.closest<HTMLAnchorElement>('a[href]');
  if (link && dialog && (!link.target || link.target === '_self')) {
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin && ['/pacientes', '/pacientes/buscar'].includes(url.pathname)
        && !link.hasAttribute('hx-get')) {
      event.preventDefault();
      openSearch(link, url);
      return;
    }
  }
  if (element?.closest('[data-patients-clear], [data-patients-retry]')) void loadSearch();
});

dialog?.addEventListener('shown.bs.modal', focusQuery);
dialog?.addEventListener('hidden.bs.modal', () => {
  trigger?.focus({ preventScroll: true });
  trigger = null;
});
function setClientOptions(select: EnhancedSelect, rows: Array<{ id: string; descripcion: string }>, label: string, disabled: boolean) {
  if (select.tomselect) {
    select.tomselect.clear(true);
    select.tomselect.clearOptions();
    select.tomselect.addOption([{ value: '', text: label }, ...rows.map(row => ({ value: row.id, text: row.descripcion }))]);
    select.tomselect.setValue('', true);
    select.tomselect.refreshOptions(false);
    if (disabled) select.tomselect.disable(); else select.tomselect.enable();
  } else {
    select.replaceChildren(new Option(label, ''), ...rows.map(row => new Option(row.descripcion, row.id)));
    select.disabled = disabled;
  }
}

document.addEventListener('change', async (event) => {
  const type = event.target;
  if (!(type instanceof HTMLSelectElement) || !type.matches('#patients-workspace [data-client-scope-type]')) return;
  const workspace = type.closest<HTMLElement>('#patients-workspace');
  const client = workspace?.querySelector<EnhancedSelect>('[data-client-scope-client]');
  const feedback = workspace?.querySelector<HTMLElement>('[data-patients-client-feedback]');
  if (!client) return;
  clientsRequest?.abort();
  clientsRequest = new AbortController();
  const request = clientsRequest;
  setClientOptions(client, [], type.value ? 'Cargando clientes…' : 'Elegí primero el tipo de cliente', true);
  if (feedback) feedback.hidden = true;
  if (!type.value) return;
  try {
    const response = await fetch(`/pacientes/clientes?clienteTipo=${encodeURIComponent(type.value)}`, {
      signal: request.signal, headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('client lookup failed');
    const rows = await response.json();
    if (request.signal.aborted || !client.isConnected) return;
    setClientOptions(client, rows, 'Todos los clientes de este tipo', false);
    if (feedback && rows.length === 0) {
      feedback.textContent = 'No hay clientes disponibles para este tipo.';
      feedback.hidden = false;
    }
  } catch {
    if (request.signal.aborted || !client.isConnected) return;
    setClientOptions(client, [], 'No se pudieron cargar los clientes', true);
    if (feedback) {
      feedback.textContent = 'No pudimos cargar los clientes. Volvé a elegir el tipo para reintentar.';
      feedback.hidden = false;
    }
  }
});

function patientRequestTarget(event: Event): HTMLElement | null {
  const target = (event as CustomEvent).detail?.target;
  return target instanceof HTMLElement && target.id === 'tabla-pacientes' && target.isConnected ? target : null;
}

document.body.addEventListener('htmx:beforeRequest', (event: Event) => {
  const results = patientRequestTarget(event);
  if (!results) return;
  activeRequest = (event as CustomEvent).detail.xhr;
  results.setAttribute('aria-busy', 'true');
  const error = document.querySelector<HTMLElement>('[data-patients-request-error]');
  if (error) error.hidden = true;
});

document.body.addEventListener('htmx:afterRequest', (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (detail?.target === dialogBody && detail.failed) {
    const error = document.querySelector<HTMLElement>('[data-patients-dialog-error]');
    if (error) error.hidden = false;
  }
  const results = patientRequestTarget(event);
  if (!results || detail.xhr !== activeRequest) return;
  results.setAttribute('aria-busy', 'false');
  const error = document.querySelector<HTMLElement>('[data-patients-request-error]');
  if (error) error.hidden = !detail.failed;
  activeRequest = undefined;
});
